import plur from 'plur';

// Flex plan profile limits from the credits design. The API does not supply them yet.
export const FREE_ACTIVE_PROFILES = 500;
export const STORED_PROFILE_CAP_FREE = 1_000;
// Funded Flex prices and the dormant allowance from the credits design. The API does not supply them yet.
export const FREE_DORMANT_PROFILES = 1_000;
export const ACTIVE_PROFILE_PRICE_CENTS = 4;
export const DORMANT_PROFILE_PRICE_CENTS = 0.45;

const CRITICAL_RUNWAY_DAYS = 3;
const LOW_RUNWAY_DAYS = 7;

export type CreditsInput = {
  balanceCents: number;
  hasFunded: boolean;
  dailySpendCents: number;
  activeProfiles: number;
  storedProfiles: number;
};

export type CreditsState =
  | { kind: 'free' }
  | { kind: 'free-limit' }
  | { kind: 'healthy' }
  | { kind: 'low'; daysLeft: number }
  | { kind: 'critical'; daysLeft: number }
  | { kind: 'empty' };

export type CreditsTone = 'default' | 'warn' | 'critical';

export type CreditsPresentation = {
  tone: CreditsTone;
  subLabel: string | null;
};

function runwayDays(balanceCents: number, dailySpendCents: number) {
  if (dailySpendCents <= 0) return null;
  // Round up, so the tone agrees with the number of days that shows.
  return Math.ceil(balanceCents / dailySpendCents);
}

export function deriveCreditsState(input: CreditsInput): CreditsState {
  if (!input.hasFunded) {
    const atLimit = input.activeProfiles >= FREE_ACTIVE_PROFILES || input.storedProfiles >= STORED_PROFILE_CAP_FREE;
    return atLimit ? { kind: 'free-limit' } : { kind: 'free' };
  }

  if (input.balanceCents <= 0) return { kind: 'empty' };

  const daysLeft = runwayDays(input.balanceCents, input.dailySpendCents);
  if (daysLeft == null || daysLeft > LOW_RUNWAY_DAYS) return { kind: 'healthy' };
  if (daysLeft <= CRITICAL_RUNWAY_DAYS) return { kind: 'critical', daysLeft };
  return { kind: 'low', daysLeft };
}

/** `unfunded`: never added funds. `empty`: added funds, balance is 0. `funded`: balance above 0. */
export type FundsStatus = 'unfunded' | 'empty' | 'funded';

export function fundsStatus(state: CreditsState): FundsStatus {
  switch (state.kind) {
    case 'free':
    case 'free-limit':
      return 'unfunded';
    case 'empty':
      return 'empty';
    case 'healthy':
    case 'low':
    case 'critical':
      return 'funded';
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

function daysLeftLabel(days: number) {
  return `≈${days} ${plur('day', days)} left`;
}

export function presentCreditsState(state: CreditsState): CreditsPresentation {
  switch (state.kind) {
    case 'free':
      return { tone: 'default', subLabel: null };
    case 'free-limit':
      return { tone: 'critical', subLabel: 'Limit reached' };
    case 'healthy':
      return { tone: 'default', subLabel: null };
    case 'low':
      return { tone: 'warn', subLabel: daysLeftLabel(state.daysLeft) };
    case 'critical':
      return { tone: 'critical', subLabel: daysLeftLabel(state.daysLeft) };
    case 'empty':
      return { tone: 'critical', subLabel: 'Account stopped' };
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}
